import React from 'react';
import { FileText, Check, Loader2, Zap, MessageSquareText } from 'lucide-react';
import type { MicroReport, ReportSectionsResponse } from '../services/api';
import astraLogo from '../ChatGPT Image Dec 11, 2025, 12_10_58 PM.png';
import { UserProfile } from './UserProfile';
import WorkspaceDocumentsSection from './WorkspaceDocumentsSection';
import BriefTranscriptModal from './BriefTranscriptModal';

type StepDisplayItem = {
  key: string;
  microreportId: string;
  reportId: string | null;
  title: string;
  status: string;
  completed: boolean;
  report: MicroReport | null;
};

interface MicroReportsPanelProps {
  reports: MicroReport[];
  selectedReport: MicroReport | null;
  onSelectReport: (report: MicroReport) => void;
  credits: number;
  isSignedIn: boolean;
  loading?: boolean;
  error?: string | null;
  stepStatuses?: {
    uid?: string;
    seq?: number;
    id?: string | number;
    microreport_id?: string;
    title?: string;
    status?: string;
    report_id?: string;
    stream_log?: string;
    is_selected?: boolean;
  }[];
  hasSession?: boolean;
  isBusy?: boolean;
  stepStage?: string | null;
  finalRegenerating?: boolean;
  journeyUnlocked?: boolean;
  customReportsEnabled?: boolean;
  userReports?: MicroReport[];
  onCreateUserReport?: () => void;
  reportSections?: ReportSectionsResponse | null;
  augmentationWorkerRunning?: boolean;
  onKillGeneration?: () => void;
  isKillingGeneration?: boolean;
  sessionId?: string | null;
  briefId?: string | null;
  workspaceName?: string;
}

type PlannedStep = NonNullable<MicroReportsPanelProps['stepStatuses']>[number];

const MicroReportsPanel: React.FC<MicroReportsPanelProps> = ({
  reports,
  selectedReport,
  onSelectReport,
  credits,
  isSignedIn,
  loading = false,
  error = null,
  stepStatuses = [],
  hasSession = false,
  isBusy = false,
  stepStage = null,
  finalRegenerating = false,
  journeyUnlocked = false,
  customReportsEnabled = false,
  userReports = [],
  onCreateUserReport,
  reportSections = null,
  augmentationWorkerRunning = false,
  onKillGeneration,
  isKillingGeneration = false,
  sessionId = null,
  briefId = null,
  workspaceName = '',
}) => {
  const listRef = React.useRef<HTMLDivElement | null>(null);
  const scrollPosRef = React.useRef<number>(0);
  const [showBriefTranscript, setShowBriefTranscript] = React.useState(false);

  const CheckboxIcon = ({ checked }: { checked: boolean }) => (
    <div
      style={{
        width: 14,
        height: 14,
        borderRadius: 3,
        border: '1.5px solid rgba(203,213,225,0.95)',
        backgroundColor: checked ? '#06b6d4' : 'transparent',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
      }}
    >
      {checked && <Check size={10} color="#ffffff" />}
    </div>
  );
  const getLatestByType = (type: string) => {
    const filtered = reports.filter((r) => (r.type || '').toLowerCase() === type);
    if (filtered.length === 0) return null;
    return filtered.slice().sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())[0];
  };

  const earlyInsightsReport = getLatestByType('early_insights');
  const framingReport = getLatestByType('framing');
  const scopeReport = getLatestByType('scope');
  // Brief and Plan exist in backend but are intentionally hidden in UI
  const finalReport = getLatestByType('final');
  
  const isSelected = (report: MicroReport | null) => !!(report && selectedReport && report.id === selectedReport.id);

  const journeyTop: Array<{ key: string; label: string; report: MicroReport | null; completed: boolean }> = [
    { key: 'scope', label: 'Scope', report: scopeReport, completed: !!scopeReport },
    { key: 'framing', label: 'Analysis Frames', report: framingReport, completed: !!framingReport },
    { key: 'early', label: 'Quick Industry Trends', report: earlyInsightsReport, completed: !!earlyInsightsReport },
  ];

  const plannedSteps: PlannedStep[] =
    stepStatuses && stepStatuses.length > 0
      ? stepStatuses
      : (reportSections?.microreports || []).map((item, idx) => ({
          seq: idx + 1,
          id: item?.id || `microreport${idx + 1}`,
          title: item?.name || `Microreport ${idx + 1}`,
          status: 'pending',
          report_id: undefined,
          stream_log: '',
          is_selected: true,
        }));
  const hasRealSteps = plannedSteps.length > 0;
  const isAutoRunning = (stepStage || '').startsWith('step_research_auto_running');
  const finalRunning = stepStage === 'final_report_running' || finalRegenerating;

  let stepItems: StepDisplayItem[] = [];

  if (hasRealSteps) {
    stepItems = plannedSteps.map((step, idx) => {
      let report: MicroReport | null = null;
      let completed = false;
      let title = '';
      let effectiveStatus = '';
      if ('seq' in step) {
        title = step.title || '';
        const reportId = (step as any).report_id as string | undefined;
        if (reportId) {
          report = reports.find((r) => r.id === reportId) || null;
        }
        completed = (step.status || '').toLowerCase() === 'completed' || !!report;
        effectiveStatus = String((step as any).status || '').toLowerCase();
        if (!effectiveStatus) {
          effectiveStatus = completed ? 'completed' : (isAutoRunning ? 'in_progress' : 'pending');
        }
        if (completed && effectiveStatus !== 'completed') {
          effectiveStatus = 'completed';
        }
      }
      return {
        key: `step-${step.seq ?? idx}`,
        microreportId: String((step as any).id || (step as any).microreport_id || `microreport${idx + 1}`),
        reportId:
          typeof (step as any).report_id === 'string' && String((step as any).report_id).trim()
            ? String((step as any).report_id).trim()
            : null,
        title,
        status: effectiveStatus,
        completed,
        report,
      } as StepDisplayItem;
    });
  }

  const archivedStepReports = reports
    // When a structured step plan is present, avoid mixing historical step reports
    // into the active queue to prevent duplicate-looking rows.
    .filter((report) => (report.type || '').toLowerCase() === 'step' && !hasRealSteps)
    .sort((a, b) => {
      const toTime = (value?: string) => {
        const t = new Date(value || '').getTime();
        return Number.isNaN(t) ? 0 : t;
      };
      return toTime(a.date) - toTime(b.date);
    });

  const cleanArchivedTitle = (title?: string) => {
    if (!title) return 'Microreport';
    const stripped = title.replace(/^step\s*\d+\s*[-:]\s*/i, '').trim();
    return stripped || 'Microreport';
  };

  // Preserve scroll position while the timeline updates
  const handleScroll = () => {
    if (listRef.current) {
      scrollPosRef.current = listRef.current.scrollTop;
    }
  };


  const allStepsCompleted = stepItems.length > 0 && stepItems.every((s) => s.completed);
  const anyStepInProgress = stepItems.some((s) => ['in_progress', 'generating', 'writing'].includes((s.status || '').toLowerCase()));
  const showMicroSpinner = hasRealSteps && (isAutoRunning || anyStepInProgress) && !finalRunning;
  const shouldShowFinalSpinner = finalRunning || (hasRealSteps && allStepsCompleted && !finalReport);
  const shouldShowFinalSection = hasRealSteps || finalReport;
  const clickableFinalReport = finalReport;

  React.useLayoutEffect(() => {
    if (listRef.current) {
      listRef.current.scrollTop = scrollPosRef.current;
    }
  }, [stepItems.length, plannedSteps.length, reports.length, showMicroSpinner]);

  let content: React.ReactNode;
  if (loading && reports.length === 0) {
    content = (
      <div className="p-4 text-center text-indigo-300">
        <div className="text-sm">Loading analyses...</div>
      </div>
    );
  } else if (error) {
    content = (
      <div className="p-4 text-center text-red-400">
        <div className="text-sm">{error}</div>
      </div>
    );
  } else {
    content = (
      <div className="space-y-3">
        {/* Research Prep group */}
        {(() => {
          const researchPrepCompleted = journeyTop.every((i) => i.completed);
          return (
            <div className="section-block">
              <div className="section-heading">Research Prep</div>
              <div className="report-list timeline-list">
                {briefId && (
                  <div className="report-chip-wrapper">
                    <button
                      onClick={() => setShowBriefTranscript(true)}
                      className="report-chip"
                      title="View the prompt conversation that prepared this workspace"
                    >
                      <MessageSquareText size={13} style={{ color: 'rgba(129, 140, 248, 0.9)', flexShrink: 0 }} />
                      <div className="report-chip-label">Prompt Conversation</div>
                    </button>
                  </div>
                )}
                {journeyTop.map((item) => {
                  const showSpinner = item.key === 'framing' && !framingReport && isBusy;
                  const isClickable = !!item.report;
                  const selected = item.report && isSelected(item.report);
                  return (
                    <div key={item.key} className="report-chip-wrapper">
                      <button
                        onClick={() => { if (isClickable && item.report) onSelectReport(item.report); }}
                        className={`report-chip ${selected ? 'report-chip-selected' : ''} ${isClickable ? '' : 'report-chip-disabled'}`}
                        disabled={!isClickable}
                      >
                        <div className={`report-chip-icon ${item.completed ? 'report-chip-icon-complete' : 'report-chip-icon-pending'}`} />
                        <div className="report-chip-label">{item.label}</div>
                        {showSpinner && <Loader2 className="w-3 h-3 text-cyan-300 ca-spin" />}
                      </button>
                    </div>
                  );
                })}
                <WorkspaceDocumentsSection sessionId={sessionId} />
              </div>
            </div>
          );
        })()}

        <div className="section-block">
          <div className="section-heading" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            Microanalyses
            {augmentationWorkerRunning && (
              <Loader2 size={14} className="ca-spin" style={{ color: '#06b6d4' }} />
            )}
          </div>
          {archivedStepReports.length > 0 && (
            <div className="report-list mb-3">
              {archivedStepReports.map((report) => {
                const selected = isSelected(report);
                return (
                  <button
                    key={`archived-${report.id}`}
                    onClick={() => onSelectReport(report)}
                    className={`report-chip ${selected ? 'report-chip-selected' : ''}`}
                  >
                    <div className="report-chip-label">{cleanArchivedTitle(report.title)}</div>
                    <div className="status-swatch status-swatch-complete" />
                  </button>
                );
              })}
            </div>
          )}
          {hasRealSteps && (
          <div className="report-list timeline-list">
            {stepItems.map((s, idx) => {
              const fallbackReport: MicroReport | null = s.reportId
                ? {
                    id: s.reportId,
                    title: s.title || `Microreport ${idx + 1}`,
                    date: '',
                    content: '',
                    type: 'step',
                  }
                : null;
              const clickableReport = s.report || fallbackReport;
              const isClickable = !!clickableReport;
              const selected = !!clickableReport && isSelected(clickableReport);
              const stepStatus = (s.status || '').toLowerCase();
              const showSpinner =
                !s.completed &&
                (stepStatus === 'in_progress' || stepStatus === 'generating' || stepStatus === 'writing');
              return (
                <div key={s.key} className="report-chip-wrapper">
                  <button
                    onClick={() => {
                      if (clickableReport) onSelectReport(clickableReport);
                    }}
                    className={`report-chip ${selected ? 'report-chip-selected' : ''} ${isClickable ? '' : 'report-chip-disabled'}`}
                    disabled={!isClickable}
                  >
                    <div className="report-chip-label">{s.title || '-'}</div>
                    {showSpinner && !finalRunning && <Loader2 className="w-3 h-3 text-cyan-300 ca-spin" />}
                    <div className={`status-swatch ${s.completed ? 'status-swatch-complete' : showSpinner ? 'status-swatch-progress status-swatch-progress-animated' : 'status-swatch-pending'}`} />
                  </button>
                </div>
              );
            })}
          </div>
          )}
        </div>

        {/* Final Report (always last) */}
        {shouldShowFinalSection && (
          <div className="section-block">
            <div className="section-heading">
              Summary
            </div>
            <div className="report-list timeline-list">
              <div className="report-chip-wrapper">
                <button
                  key="final"
                  onClick={() => {
                    if (clickableFinalReport) {
                      onSelectReport(clickableFinalReport);
                    }
                  }}
                  className={`report-chip ${clickableFinalReport && isSelected(clickableFinalReport) ? 'report-chip-selected' : ''} ${clickableFinalReport ? '' : 'report-chip-disabled'}`}
                  disabled={!clickableFinalReport}
                >
                  <div className="report-chip-icon report-chip-icon-summary" />
                  <div className="report-chip-label">Final Analysis</div>
                  {(!clickableFinalReport && shouldShowFinalSpinner) && <Loader2 className="w-3 h-3 text-cyan-300 ca-spin" />}
                  <div className={`status-swatch ${
                    clickableFinalReport
                      ? 'status-swatch-complete'
                      : shouldShowFinalSpinner
                        ? 'status-swatch-progress'
                        : 'status-swatch-pending'
                  }`} />
                </button>
              </div>
            </div>
          </div>
        )}

        {customReportsEnabled && (
          <div className="section-block">
            <div className="section-heading" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <span style={{ flex: '1 1 auto' }}>Create Reports</span>
              <button
                type="button"
                onClick={() => onCreateUserReport?.()}
                aria-label="Create custom analysis"
                style={{
                  border: 'none',
                  background: 'transparent',
                  color: '#f8fafc',
                  fontSize: '1.25rem',
                  lineHeight: 1,
                  padding: 0,
                  margin: 0,
                  cursor: 'pointer',
                }}
              >
                +
              </button>
            </div>
            <div className="report-list">
              {userReports.length > 0 ? (
                userReports.map((report) => {
                  const selected = isSelected(report);
                  return (
                    <button
                      key={report.id}
                      onClick={() => onSelectReport(report)}
                      className={`report-chip ${selected ? 'report-chip-selected' : ''}`}
                    >
                      <div className="report-chip-label">{report.title || 'Untitled custom analysis'}</div>
                      <div className="status-swatch status-swatch-progress" />
                    </button>
                  );
                })
              ) : (
                <div className="report-placeholder text-slate-400 text-xs">
                  Create a report and edit it in the document viewer.
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="w-64 bg-slate-960/70 border-r border-slate-800/60 flex flex-col">
      {showBriefTranscript && briefId && (
        <BriefTranscriptModal
          briefId={briefId}
          workspaceName={workspaceName}
          onClose={() => setShowBriefTranscript(false)}
        />
      )}
      <div className="p-4 border-b border-slate-800/60 flex items-center gap-2">
        <div className="report-header-icon">
          <FileText className="w-4 h-4" />
        </div>
        <div>
          <h2 className="text-sm font-semibold text-white tracking-widest uppercase">astra Analysis Documents</h2>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto" ref={listRef} onScroll={handleScroll}>
        <div className="p-3 space-y-3">{content}</div>
      </div>

      <div className="p-4 border-t border-slate-800/60 bg-slate-960/80">
        <div className="flex items-center justify-start">
          <div className="flex items-center gap-2">
            {isSignedIn && (
              <>
                <UserProfile />
                <div
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '0 2px',
                    color: '#cbd5e1',
                    fontSize: '13px',
                    fontWeight: 500,
                    letterSpacing: '0.01em',
                    cursor: 'default',
                    userSelect: 'none'
                  }}
                >
                  <Zap size={13} color="#a5b4fc" />
                  <span style={{ opacity: 0.8 }}>Credits</span>
                  <span style={{ color: '#e0e7ff', fontWeight: 700 }}>{credits.toLocaleString()}</span>
                </div>
                <div className="flex items-center" style={{ marginLeft: '2px' }}>
                  <img
                    src={astraLogo}
                    alt="Astra logo"
                    style={{ width: 34, height: 34, borderRadius: 9, objectFit: 'cover', opacity: 0.98 }}
                  />
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MicroReportsPanel;


